/**
 * OperatorTabBar Component
 *
 * Fixed bottom tab navigation for the operator portal.
 * Three tabs by default: Goals, Progress, Content (Phase 21.2 routes).
 * Accepts custom tabs via props for temporary route mapping in 21.0.3.
 *
 * Uses tracking-wide (not tracking-widest) per design review.
 * Haptic feedback via navigator.vibrate on supported devices.
 */

import { Link, useLocation } from 'react-router-dom';
import { Target, TrendingUp, BookOpen } from 'lucide-react';
import { cn } from '../../../lib/utils';

export interface TabItem {
  /** Tab label text */
  label: string;
  /** Route path for this tab */
  href: string;
  /** Lucide icon component */
  icon: React.ComponentType<{ size?: number; className?: string; strokeWidth?: number }>;
  /** When true, only match pathname === href (prevents /operator matching /operator/training) */
  exact?: boolean;
}

interface OperatorTabBarProps {
  /** Tab configuration. Defaults to Goals/Progress/Content for Phase 21.2 routes.
   *  Override in 21.0.3 to temporarily map to existing routes. */
  tabs?: TabItem[];
  /** Additional CSS classes for the nav wrapper */
  className?: string;
}

const DEFAULT_TABS: TabItem[] = [
  { label: 'Goals', href: '/operator/goals', icon: Target },
  { label: 'Progress', href: '/operator/progress', icon: TrendingUp },
  { label: 'Content', href: '/operator/content', icon: BookOpen },
];

export function OperatorTabBar({ tabs = DEFAULT_TABS, className }: OperatorTabBarProps) {
  const location = useLocation();

  return (
    <nav
      aria-label="Main navigation"
      className={cn(
        'fixed bottom-0 left-0 right-0 z-50',
        'bg-op-card/95 backdrop-blur-lg shadow-[0_-1px_3px_rgba(0,0,0,0.08)]',
        className,
      )}
      style={{ paddingBottom: 'max(16px, env(safe-area-inset-bottom))' }}
    >
      <div className="flex items-end justify-around h-[64px]">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          const isActive = tab.exact
            ? location.pathname === tab.href
            : location.pathname === tab.href ||
              location.pathname.startsWith(tab.href + '/');

          return (
            <Link
              key={tab.href}
              to={tab.href}
              aria-current={isActive ? 'page' : undefined}
              onClick={() => {
                if ('vibrate' in navigator) {
                  navigator.vibrate(10);
                }
              }}
              className={cn(
                'flex flex-1 flex-col items-center justify-center gap-1 py-2',
                'focus-visible:ring-2 focus-visible:ring-guild-green focus-visible:ring-offset-2 focus-visible:rounded-lg',
                'transition-colors duration-200',
              )}
            >
              {/* Active indicator bar */}
              <div
                className={cn(
                  'h-[3px] w-6 rounded-full transition-all duration-300',
                  isActive ? 'bg-guild-green' : 'bg-transparent',
                )}
              />

              <Icon
                size={24}
                strokeWidth={1.5}
                className={isActive ? 'text-guild-green' : 'text-gray-400'}
              />

              <span
                className={cn(
                  'text-[11px] font-semibold tracking-wide uppercase',
                  isActive ? 'text-guild-green' : 'text-gray-400',
                )}
              >
                {tab.label}
              </span>
            </Link>
          );
        })}
      </div>
    </nav>
  );
}
